import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LayoutDashboard, FileText, MessageSquare, Calculator, LogOut, Lock,
  MessagesSquare, ChevronDown, ChevronUp, Phone, Mail, Download, Newspaper,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { BlogManager } from "./AdminBlog";
import { logError } from "../lib/logger";
import { FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

const estimateSummary = (e) =>
  [
    e.squareFeet,
    e.bedrooms && `${e.bedrooms} bed`,
    e.bathrooms && `${e.bathrooms} bath`,
    e.extras?.length ? `Extras: ${e.extras.join(", ")}` : "",
    e.notes,
  ].filter(Boolean).join(" · ");

// ─── Login ────────────────────────────────────────────────────────────────────
const INPUT_STYLE = {
  fontFamily: FONT_BODY,
  border: "1px solid rgba(27,58,107,0.22)",
  borderRadius: "2px",
  fontSize: "14px",
  color: "#2D2D2D",
};

const LoginForm = ({ email, setEmail, password, setPassword, error, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <input
      type="email"
      placeholder="Email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      data-testid="admin-email-input"
      className="w-full px-4 py-3"
      style={INPUT_STYLE}
      required
    />
    <input
      type="password"
      placeholder="Password"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      data-testid="admin-password-input"
      className="w-full px-4 py-3"
      style={INPUT_STYLE}
      required
    />
    {error && (
      <div data-testid="admin-login-error" className="px-3 py-2 text-xs" style={{ background: "rgba(239,68,68,0.07)", color: "#DC2626", fontFamily: FONT_BODY }}>
        {error}
      </div>
    )}
    <button
      type="submit"
      data-testid="admin-login-btn"
      disabled={loading}
      className="w-full py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
      style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
    >
      {loading ? "Signing in..." : "Sign In"}
    </button>
  </form>
);

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      onLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: NAVY }} data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-white p-8 shadow-2xl">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <div className="flex items-center justify-center gap-2 mb-6">
          <Lock size={14} style={{ color: GOLD }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#6B7280", fontFamily: FONT_HEAD }}>
            Admin Panel
          </span>
        </div>
        <LoginForm
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          error={error}
          loading={loading}
          onSubmit={submit}
        />
      </div>
    </div>
  );
};

// ─── Dashboard pieces ─────────────────────────────────────────────────────────
const StatCard = ({ label, value, Icon, testid }) => (
  <div data-testid={testid} className="bg-white p-5 shadow-sm" style={{ border: "1px solid rgba(27,58,107,0.08)", borderTop: `3px solid ${GOLD}` }}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-3xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{value}</div>
        <div className="text-xs mt-1 uppercase tracking-wider font-semibold" style={{ color: "#9CA3AF", fontFamily: FONT_BODY }}>{label}</div>
      </div>
      <Icon size={26} style={{ color: GOLD, opacity: 0.7 }} />
    </div>
  </div>
);

const StatsGrid = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    <StatCard label="Quote Requests" value={stats?.quotes ?? "—"} Icon={FileText} testid="stat-quotes" />
    <StatCard label="Chatbot Leads" value={stats?.leads ?? "—"} Icon={MessageSquare} testid="stat-leads" />
    <StatCard label="Estimates" value={stats?.estimates ?? "—"} Icon={Calculator} testid="stat-estimates" />
    <StatCard label="Chat Sessions" value={stats?.chatSessions ?? "—"} Icon={MessagesSquare} testid="stat-chats" />
  </div>
);

const ContactLine = ({ phone, email }) => (
  <div className="flex flex-wrap gap-4 mt-1 text-xs" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
    {phone && (
      <a href={`tel:${phone}`} className="flex items-center gap-1 hover:text-[#D4A843]">
        <Phone size={11} /> {phone}
      </a>
    )}
    {email && (
      <a href={`mailto:${email}`} className="flex items-center gap-1 hover:text-[#D4A843]">
        <Mail size={11} /> {email}
      </a>
    )}
  </div>
);

const RecordCard = ({ title, subtitle, phone, email, body, timestamp, testid }) => (
  <div data-testid={testid} className="bg-white p-5 shadow-sm" style={{ border: "1px solid rgba(27,58,107,0.08)" }}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-bold text-sm" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{title}</div>
        {subtitle && (
          <div className="text-xs mt-0.5 font-semibold" style={{ color: GOLD, fontFamily: FONT_BODY }}>{subtitle}</div>
        )}
        <ContactLine phone={phone} email={email} />
      </div>
      <span className="text-xs whitespace-nowrap" style={{ color: "#9CA3AF", fontFamily: FONT_BODY }}>{fmtDate(timestamp)}</span>
    </div>
    {body && (
      <p className="text-xs mt-3 leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>{body}</p>
    )}
  </div>
);

const ChatSession = ({ session }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white shadow-sm" style={{ border: "1px solid rgba(27,58,107,0.08)" }}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`chat-session-${session.sessionId}`}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div>
          <div className="text-sm font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Conversation · {session.count} messages
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#9CA3AF", fontFamily: FONT_BODY }}>
            Last activity: {fmtDate(session.lastActivity)}
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: NAVY }} /> : <ChevronDown size={16} style={{ color: NAVY }} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 max-h-80 overflow-y-auto">
          {session.messages.map((m, i) => (
            <div key={`${session.sessionId}-${i}`} className="text-xs p-2.5 leading-relaxed" style={{
              background: m.role === "user" ? "rgba(27,58,107,0.06)" : "rgba(212,168,67,0.08)",
              fontFamily: FONT_BODY,
              color: "#2D2D2D",
            }}>
              <span className="font-bold" style={{ color: m.role === "user" ? NAVY : "#b8922a" }}>
                {m.role === "user" ? "Client" : "Cindy"}:
              </span>{" "}
              {m.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="text-center py-14 text-sm" style={{ color: "#9CA3AF", fontFamily: FONT_BODY }} data-testid="admin-empty-state">
    {text}
  </div>
);

const TABS = [
  { id: "quotes", label: "Quotes", Icon: FileText },
  { id: "leads", label: "Chatbot Leads", Icon: MessageSquare },
  { id: "estimates", label: "Estimates", Icon: Calculator },
  { id: "chats", label: "Conversations", Icon: MessagesSquare },
  { id: "blog", label: "Blog", Icon: Newspaper },
];

const TabContent = ({ tab, data }) => {
  if (tab === "blog") return <BlogManager api={api} />;
  const items = data[tab] || [];
  if (!items.length) return <EmptyState text="Nothing here yet." />;

  if (tab === "quotes")
    return items.map((q) => (
      <RecordCard key={q.id} testid={`quote-row-${q.id}`} title={q.fullName} subtitle={q.service}
        phone={q.phone} email={q.email} body={q.message} timestamp={q.timestamp} />
    ));
  if (tab === "leads")
    return items.map((l) => (
      <RecordCard key={l.id} testid={`lead-row-${l.id}`} title={l.name} subtitle={l.service || "Chatbot Lead"}
        phone={l.phone} email={l.email} timestamp={l.timestamp} />
    ));
  if (tab === "estimates")
    return items.map((e) => (
      <RecordCard key={e.id} testid={`estimate-row-${e.id}`} title={e.fullName}
        subtitle={`${e.propertyType} · ${e.frequency}`}
        phone={e.phone} email={e.email} body={estimateSummary(e)} timestamp={e.timestamp} />
    ));
  return items.map((s) => <ChatSession key={s.sessionId} session={s} />);
};

const EXPORTABLE = ["quotes", "leads", "estimates"];

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ExportButton = ({ tab }) => {
  const [busy, setBusy] = useState(false);
  if (!EXPORTABLE.includes(tab)) return null;

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/export/${tab}`, { responseType: "blob" });
      downloadBlob(res.data, `cindyrella-${tab}.csv`);
    } catch (err) {
      logError("CSV export failed", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={download}
      disabled={busy}
      data-testid="admin-export-csv-btn"
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-2 transition-all hover:bg-[#1B3A6B] hover:text-white disabled:opacity-50 ml-auto"
      style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}
    >
      <Download size={14} />
      {busy ? "Exporting..." : "Export CSV"}
    </button>
  );
};

const TabBar = ({ tab, setTab }) => (
  <div className="flex flex-wrap gap-2 mb-6 items-center">
    {TABS.map(({ id, label, Icon }) => (
      <button
        key={id}
        data-testid={`tab-${id}`}
        onClick={() => setTab(id)}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-all"
        style={{
          background: tab === id ? NAVY : "#FFFFFF",
          color: tab === id ? "#FFFFFF" : "#2D2D2D",
          border: "1px solid rgba(27,58,107,0.15)",
          fontFamily: FONT_BODY,
        }}
      >
        <Icon size={14} />
        {label}
      </button>
    ))}
    <ExportButton tab={tab} />
  </div>
);

const AdminHeader = ({ onLogout }) => (
  <header className="flex items-center justify-between px-4 md:px-8 py-4 bg-white shadow-sm">
    <div className="flex items-center gap-3">
      <Logo />
      <span className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-2.5 py-1" style={{ background: "rgba(212,168,67,0.15)", color: "#b8922a", fontFamily: FONT_HEAD }}>
        <LayoutDashboard size={12} />
        Admin
      </span>
    </div>
    <button
      onClick={onLogout}
      data-testid="admin-logout-btn"
      className="flex items-center gap-2 px-4 py-2 text-sm font-bold border-2 transition-all hover:bg-[#1B3A6B] hover:text-white"
      style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}
    >
      <LogOut size={14} />
      Logout
    </button>
  </header>
);

const fetchDashboard = async () => {
  const [statsRes, quotesRes, leadsRes, estimatesRes, chatsRes] = await Promise.all([
    api.get("/admin/stats"),
    api.get("/admin/quotes"),
    api.get("/admin/leads"),
    api.get("/admin/estimates"),
    api.get("/admin/chats"),
  ]);
  return {
    stats: statsRes.data,
    data: {
      quotes: quotesRes.data.items,
      leads: leadsRes.data.items,
      estimates: estimatesRes.data.items,
      chats: chatsRes.data.items,
    },
  };
};

const isUnauthorized = (err) => err.response?.status === 401;

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("quotes");
  const [data, setData] = useState({});

  const applyDashboard = useCallback((result) => {
    setStats(result.stats);
    setData(result.data);
  }, [setStats, setData]);

  const load = useCallback(
    () => fetchDashboard().then(applyDashboard).catch((err) => isUnauthorized(err) && onLogout()),
    [applyDashboard, onLogout]
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen" style={{ background: "#F3F5F9" }} data-testid="admin-dashboard">
      <AdminHeader onLogout={onLogout} />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <StatsGrid stats={stats} />
        <TabBar tab={tab} setTab={setTab} />
        <div className="space-y-3" data-testid={`admin-tab-content-${tab}`}>
          <TabContent tab={tab} data={data} />
        </div>
      </main>
    </div>
  );
};

const checkSession = () => api.get("/auth/me").then(() => true).catch(() => false);
const requestLogout = () => api.post("/auth/logout").catch((err) => logError("Logout request failed", err));

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    checkSession().then(setAuthed);
  }, [setAuthed]);

  const logout = useCallback(() => requestLogout().then(() => setAuthed(false)), [setAuthed]);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }} data-testid="admin-loading">
        <span className="text-sm text-white/70" style={{ fontFamily: FONT_BODY }}>Loading…</span>
      </div>
    );
  }
  return authed ? <AdminDashboard onLogout={logout} /> : <AdminLogin onLogin={() => setAuthed(true)} />;
}
